import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, inject, signal } from '@angular/core';
import { TenantUserRoles } from '@buspro/shared/common';
import { PlaceInListDto, PlaceTypeEnum, PlacesApi } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzDrawerComponent, NzDrawerContentDirective } from 'ng-zorro-antd/drawer';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { AvatarModule } from 'ngx-avatars';
import { lastValueFrom } from 'rxjs';

interface PresenceMember {
  id: string;
  info: {
    name: string;
    tenantId: string;
    tenantName: string;
    terminalId?: string | null;
    role?: string;
  };
}

interface TenantPresence {
  tenantId: string;
  tenantName: string;
  onlineUsers: PresenceMember[];
}

@Component({
  selector: 'tenant-online-users',
  standalone: true,
  imports: [NgIf, NgFor, NzDrawerComponent, NzDrawerContentDirective, AvatarModule, NzTagComponent],
  template: `
    <button (click)="showDrawer.set(true)" class="btn btn-ghost theme-hover-bg">
      <span class="fe fe-users mr-2"></span>
      <span>Online Users</span>
      @if(onlineUsers > 0){
        <div class="badge badge-primary ml-2">{{onlineUsers}}</div>
      }@else{
        <div class="badge badge-ghost ml-2"><span class="loading loading-ring loading-sm"></span></div>
      }
    </button>

    <nz-drawer
      [nzVisible]="showDrawer()"
      [nzTitle]="drawerTitle"
      [nzPlacement]="'right'"
      [nzWidth]="400"
      (nzOnClose)="onDrawerClose()"
      (nzVisibleChange)="onDrawerVisibleChange($event)"
    >
      <ng-template #drawerTitle>
        <div class="flex items-center gap-3">
          <span class="fe fe-users text-xl text-primary"></span>
          <div>
            <h3 class="text-lg font-semibold m-0">Online Users</h3>
            <p class="text-sm text-gray-500 m-0">{{ getTotalUsers() }} currently online</p>
          </div>
        </div>
      </ng-template>

      <ng-container *nzDrawerContent>
        @if(currentTenantUsers().length > 0) {
          <div class="space-y-4">
            <!-- Current Tenant Users -->
            <div>
              <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span class="fe fe-building text-primary"></span>
                {{ currentTenantName() }} ({{ currentTenantUsers().length }})
              </h4>
              <div class="space-y-2">
                @for (user of currentTenantUsers(); track user.id) {
                  <div class="flex items-center gap-3 p-3 rounded-lg theme-hover-bg transition-colors duration-200 border theme-border">
                    <div class="relative">
                      <ngx-avatars [name]="user.info.name" [size]="40" class="rounded-full"></ngx-avatars>
                      <div class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 theme-border"></div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">{{ user.info.name }}</p>
                      <div class="flex items-center gap-2 mt-1 flex-wrap">
                        @if(user.info.role) {
                          <nz-tag [nzColor]="getRoleColor(user.info.role)" class="text-xs">
                            <span class="fe fe-user mr-1"></span>
                            {{ user.info.role }}
                          </nz-tag>
                        }
                        <nz-tag [nzColor]="getAssignmentColor(getUserAssignment(user))" class="text-xs">
                          <span class="fe fe-map-pin mr-1"></span>
                          {{ getUserAssignment(user) }}
                        </nz-tag>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        } @else {
          <div class="flex flex-col items-center justify-center py-12 text-center">
            <span class="fe fe-users text-6xl text-gray-300 mb-4"></span>
            <h3 class="text-lg font-semibold text-gray-700 mb-2">No Users Online</h3>
            <p class="text-sm text-gray-500">There are currently no users online in your tenant.</p>
          </div>
        }
      </ng-container>
    </nz-drawer>
  `,
  styles: `
    :host {
      display: inline-block;
    }
  `,
})
export class OnlineUsersComponent implements OnInit, OnChanges {
  @Input() onlineUsers = 0;
  @Input() tenantPresence: TenantPresence[] = [];
  @Input() currentTenantId: string | null = null;

  private placesApi = inject(PlacesApi);

  showDrawer = signal(false);

  currentTenantUsers = signal<PresenceMember[]>([]);
  currentTenantName = signal<string>('');
  otherTenantsUsers = signal<TenantPresence[]>([]);

  // Cache for terminals (needed to map terminalId to terminal name)
  private terminalsCache: PlaceInListDto[] = [];

  terminalsQuery = injectQuery(() => ({
    queryKey: ['terminals-for-online-users'],
    queryFn: () => lastValueFrom(this.placesApi.placeControllerGetTenantPlaces({ type: PlaceTypeEnum.Terminal })),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: this.showDrawer(), // Only fetch when drawer is open
  }));

  ngOnInit() {
    this.updateUsersList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tenantPresence'] || changes['currentTenantId']) {
      this.updateUsersList();
    }
  }

  onDrawerVisibleChange(visible: boolean) {
    this.showDrawer.set(visible);
    if (visible) {
      this.onDrawerOpen();
    }
  }

  onDrawerClose() {
    this.showDrawer.set(false);
  }

  async onDrawerOpen() {
    // Update terminals cache when drawer opens (needed for terminal name lookup)
    if (this.terminalsQuery.data()) {
      this.terminalsCache = this.terminalsQuery.data() || [];
    }

    // No need to fetch user details - all data comes from presence channel
    // Presence data now includes terminalId and role directly
  }

  private updateUsersList() {
    if (!this.currentTenantId || !this.tenantPresence || this.tenantPresence.length === 0) {
      this.currentTenantUsers.set([]);
      this.currentTenantName.set('');
      return;
    }

    const currentTenant = this.tenantPresence.find((tenant) => tenant.tenantId === this.currentTenantId);

    if (currentTenant) {
      this.currentTenantUsers.set(currentTenant.onlineUsers);
      this.currentTenantName.set(currentTenant.tenantName);
    } else {
      this.currentTenantUsers.set([]);
      this.currentTenantName.set('');
    }
  }

  getTotalUsers(): number {
    return this.tenantPresence.reduce((total, tenant) => total + tenant.onlineUsers.length, 0);
  }

  getUserAssignment(user: PresenceMember): string {
    // Use presence data directly - no API calls needed
    const role = user.info.role;
    const terminalId = user.info.terminalId;

    // Special handling for Tenant Admins - they can be global but also assigned to terminals
    if (role === TenantUserRoles.TENANT_ADMIN) {
      if (terminalId) {
        const terminalName = this.getTerminalNameSafe(terminalId);
        return `Global + ${terminalName}`;
      }
      return 'Global';
    }

    // Other global roles don't have terminal assignments
    if (role && this.isGlobalRole(role as TenantUserRoles)) {
      return 'Global';
    }

    // Conductor roles can have terminal assignments (synced from bus terminal)
    if (role === TenantUserRoles.TENANT_CONDUCTOR) {
      if (terminalId) {
        return this.getTerminalNameSafe(terminalId);
      }
      return 'Global';
    }

    // Terminal-based roles (managers and clerks)
    return this.getTerminalNameSafe(terminalId);
  }

  private getTerminalNameSafe(terminalId: string | null | undefined): string {
    if (terminalId === null || terminalId === undefined || !terminalId || terminalId.trim() === '') {
      return 'Unassigned';
    }

    const terminal = this.terminalsCache.find((t) => t?.id === terminalId);
    return terminal?.name || 'Unknown Terminal';
  }

  private isGlobalRole(role: TenantUserRoles): boolean {
    const globalRoles = [
      TenantUserRoles.TENANT_ADMIN,
      TenantUserRoles.TENANT_DIRECTOR,
      TenantUserRoles.TENANT_MANAGEMENT_MEMBER,
      TenantUserRoles.TENANT_ACCOUNTANT,
    ];
    return globalRoles.includes(role);
  }

  getAssignmentColor(assignment: string): string {
    if (assignment === 'Global' || assignment.startsWith('Global +')) {
      return 'blue';
    }
    if (assignment === 'Unassigned') {
      return 'default';
    }
    if (assignment === 'Loading...') {
      return 'default';
    }
    if (assignment === 'Unknown Terminal') {
      return 'orange';
    }
    return 'green';
  }

  getRoleColor(role: string): string {
    // Color code by role type
    if (role === TenantUserRoles.TENANT_ADMIN) {
      return 'red';
    }
    if (role === TenantUserRoles.TENANT_DIRECTOR || role === TenantUserRoles.TENANT_MANAGEMENT_MEMBER) {
      return 'purple';
    }
    if (role === TenantUserRoles.TENANT_TERMINAL_MANAGER) {
      return 'blue';
    }
    if (role === TenantUserRoles.TENANT_ACCOUNTANT) {
      return 'cyan';
    }
    if (role === TenantUserRoles.TENANT_PARCEL_MANAGER || role === TenantUserRoles.TERMINAL_PARCEL_CLERK) {
      return 'orange';
    }
    if (role === TenantUserRoles.TERMINAL_BOOKING_CLERK) {
      return 'geekblue';
    }
    return 'default';
  }
}
